import { useState } from 'react';
import {
  MARKET_DATA_SOURCE_LABELS,
  type DossierScenario,
  type DossierStatut,
  type MarketDataDto,
  type MarketDataSource,
} from '../../lib/api-types';
import type { MarketDataCreateInput } from '../../lib/api';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { Table, THead, TBody, TR, TH, TD } from '../../components/ui/Table';
import {
  useCreateMarketData,
  useDeleteMarketData,
  useMarketData,
  useUpdateMarketData,
} from './hooks';

const SOURCES: MarketDataSource[] = [
  'DGI_FISCAL',
  'AGENCE_IMMOBILIERE',
  'ENQUETE_TERRAIN',
  'BASE_INTERNE',
];

const LOCKED_STATUSES: DossierStatut[] = ['FINALISE', 'ARCHIVE'];

interface FormState {
  id: string | null; // null → create; otherwise edit
  source: MarketDataSource;
  localisation: string;
  superficieM2: string;
  prix: string;
  prixM2: string;
  dateReleve: string;
  observation: string;
}

const EMPTY_FORM: FormState = {
  id: null,
  source: 'AGENCE_IMMOBILIERE',
  localisation: '',
  superficieM2: '',
  prix: '',
  prixM2: '',
  dateReleve: '',
  observation: '',
};

function num(value: string | null | undefined): string {
  return value && value.length > 0 ? value : '—';
}

/**
 * « Marché » — saisie manuelle des données de marché du Rapport (REQ-F-061 / BR-035). Les 4 sources
 * (DGI/fiscal, agences, enquêtes terrain, base interne) sont saisies par l'expert. Ces données sont un
 * contenu NARRATIF du Rapport : elles n'alimentent PAS le calcul ni son hash (le serveur reste
 * l'autorité). Édition possible tant que le dossier n'est pas verrouillé ; lecture seule ensuite.
 */
export function MarketDataTab({
  dossierId,
  scenario,
  statut,
}: {
  dossierId: string;
  scenario: DossierScenario;
  statut: DossierStatut;
}): JSX.Element {
  const locked = LOCKED_STATUSES.includes(statut);
  const entries = useMarketData(dossierId);
  const create = useCreateMarketData(dossierId);
  const update = useUpdateMarketData(dossierId);
  const remove = useDeleteMarketData(dossierId);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const rows = entries.data?.items ?? [];
  const mutating = create.isPending || update.isPending || remove.isPending;
  const mutationError = create.error ?? update.error ?? remove.error;

  const buildInput = (): MarketDataCreateInput => ({
    source: form.source,
    localisation: form.localisation.trim(),
    ...(form.superficieM2.trim() !== '' ? { superficieM2: form.superficieM2.trim() } : {}),
    ...(form.prix.trim() !== '' ? { prix: form.prix.trim() } : {}),
    ...(form.prixM2.trim() !== '' ? { prixM2: form.prixM2.trim() } : {}),
    ...(form.dateReleve.trim() !== '' ? { dateReleve: form.dateReleve.trim() } : {}),
    ...(form.observation.trim() !== '' ? { observation: form.observation.trim() } : {}),
  });

  const canSubmit = form.localisation.trim() !== '' && !mutating && !locked;

  const submit = (): void => {
    if (!canSubmit) return;
    if (form.id === null) {
      create.mutate(buildInput(), { onSuccess: () => setForm(EMPTY_FORM) });
    } else {
      update.mutate({ id: form.id, input: buildInput() }, { onSuccess: () => setForm(EMPTY_FORM) });
    }
  };

  const startEdit = (e: MarketDataDto): void => {
    setForm({
      id: e.id,
      source: e.source,
      localisation: e.localisation,
      superficieM2: e.superficieM2 ?? '',
      prix: e.prix ?? '',
      prixM2: e.prixM2 ?? '',
      dateReleve: e.dateReleve ?? '',
      observation: e.observation ?? '',
    });
  };

  if (scenario === 'PV_SEUL') {
    return (
      <Alert variant="info">
        Les données de marché appartiennent au Rapport d’expertise. Ce dossier est en scénario « PV
        seul » : la saisie du marché n’est pas applicable.
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {locked ? (
        <Alert variant="info">
          Le dossier est verrouillé : les données de marché sont en lecture seule.
        </Alert>
      ) : null}

      {/* A. SAISIE ------------------------------------------------------------ */}
      {!locked ? (
        <Card>
          <CardHeader>
            <CardTitle>
              {form.id === null ? 'Ajouter une donnée de marché' : 'Modifier la donnée de marché'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="md-source">Source</Label>
                <Select
                  id="md-source"
                  value={form.source}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, source: e.target.value as MarketDataSource }))
                  }
                >
                  {SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {MARKET_DATA_SOURCE_LABELS[s]}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="md-loc">Localisation</Label>
                <Input
                  id="md-loc"
                  value={form.localisation}
                  onChange={(e) => setForm((f) => ({ ...f, localisation: e.target.value }))}
                  placeholder="Quartier, secteur…"
                />
              </div>
              <div>
                <Label htmlFor="md-sup">Superficie (m²)</Label>
                <Input
                  id="md-sup"
                  inputMode="decimal"
                  value={form.superficieM2}
                  onChange={(e) => setForm((f) => ({ ...f, superficieM2: e.target.value }))}
                  placeholder="ex. 300"
                />
              </div>
              <div>
                <Label htmlFor="md-prix">Prix (FCFA)</Label>
                <Input
                  id="md-prix"
                  inputMode="decimal"
                  value={form.prix}
                  onChange={(e) => setForm((f) => ({ ...f, prix: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="md-prixm2">Prix/m² (FCFA)</Label>
                <Input
                  id="md-prixm2"
                  inputMode="decimal"
                  value={form.prixM2}
                  onChange={(e) => setForm((f) => ({ ...f, prixM2: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="md-date">Date de relevé</Label>
                <Input
                  id="md-date"
                  type="date"
                  value={form.dateReleve}
                  onChange={(e) => setForm((f) => ({ ...f, dateReleve: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="md-obs">Observation</Label>
              <Input
                id="md-obs"
                value={form.observation}
                onChange={(e) => setForm((f) => ({ ...f, observation: e.target.value }))}
                placeholder="Note libre (facultatif)"
              />
            </div>
            <p className="text-xs text-slate-500">
              Saisie manuelle uniquement (BR-035). Le prix/m² est saisi tel quel — aucune donnée de
              marché n’entre dans le calcul ni son hash.
            </p>
            <div className="flex gap-2">
              <Button type="button" onClick={submit} disabled={!canSubmit}>
                {mutating ? 'Enregistrement…' : form.id === null ? 'Ajouter' : 'Enregistrer'}
              </Button>
              {form.id !== null ? (
                <Button type="button" variant="secondary" onClick={() => setForm(EMPTY_FORM)}>
                  Annuler
                </Button>
              ) : null}
            </div>
            {mutationError ? (
              <Alert variant="error" details={toDisplayError(mutationError).details}>
                {toDisplayError(mutationError).message}
              </Alert>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* B. LISTE PAR SOURCE -------------------------------------------------- */}
      {entries.isError ? (
        <Alert variant="error">{toDisplayError(entries.error).message}</Alert>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">Aucune donnée de marché saisie.</p>
      ) : (
        SOURCES.filter((s) => rows.some((r) => r.source === s)).map((s) => (
          <Card key={s}>
            <CardHeader>
              <CardTitle>{MARKET_DATA_SOURCE_LABELS[s]}</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <THead>
                  <TR>
                    <TH>Localisation</TH>
                    <TH>Superficie</TH>
                    <TH>Prix</TH>
                    <TH>Prix/m²</TH>
                    <TH>Relevé</TH>
                    <TH>Observation</TH>
                    {!locked ? <TH>Actions</TH> : null}
                  </TR>
                </THead>
                <TBody>
                  {rows
                    .filter((r) => r.source === s)
                    .map((r) => (
                      <TR key={r.id}>
                        <TD>{r.localisation}</TD>
                        <TD>{num(r.superficieM2)}</TD>
                        <TD>{num(r.prix)}</TD>
                        <TD>{num(r.prixM2)}</TD>
                        <TD>{num(r.dateReleve)}</TD>
                        <TD>{num(r.observation)}</TD>
                        {!locked ? (
                          <TD>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className="text-xs text-brand-600 hover:underline"
                                onClick={() => startEdit(r)}
                              >
                                Modifier
                              </button>
                              <button
                                type="button"
                                className="text-xs text-red-600 hover:underline disabled:opacity-50"
                                disabled={mutating}
                                onClick={() => remove.mutate(r.id)}
                              >
                                Supprimer
                              </button>
                            </div>
                          </TD>
                        ) : null}
                      </TR>
                    ))}
                </TBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
